import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, take, switchMap, map, catchError, timeout } from 'rxjs/operators';

import { AppState } from '@fcom/core';
import { Profile, ProfileTier, LoginStatus } from '@fcom/core-api/login';
import { loginStatus, profile } from '@fcom/core/selectors';
import { LoginService } from '@fcom/common/login';
import { recurseLangParam } from '@fcom/common';
import { finShare } from '@fcom/rx';

@Injectable()
export class PlusUserGuard implements CanActivate {
  static readonly GET_LOGIN_STATUS_TIMEOUT = 20000;

  constructor(
    private store$: Store<AppState>,
    private loginService: LoginService,
    private router: Router,
    @Inject(PLATFORM_ID) private platform: object
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    if (isPlatformServer(this.platform)) {
      return of(true);
    }

    const lang = recurseLangParam(route);

    return this.store$.pipe(
      loginStatus(),
      finShare(),
      // throw immediately if login status is resolved to NOT_LOGGED_IN
      timeout(PlusUserGuard.GET_LOGIN_STATUS_TIMEOUT),
      map((status) => {
        if (status === LoginStatus.NOT_LOGGED_IN) {
          throw new Error('User not logged in');
        }
        return status;
      }),
      filter((status) => status === LoginStatus.LOGGED_IN),
      // if user is logged in, fetch profile
      switchMap(() => this.store$.pipe(profile(), take(1))),
      // pass through all tiers expect Finnair ID
      map((memberProfile: Profile) => {
        if (
          [ProfileTier.ACCOUNT, ProfileTier.CORPORATE].includes(memberProfile.tier) ||
          (memberProfile.partStatus && memberProfile.partStatus.code !== 'SUCCESS')
        ) {
          throw new Error('Invalid plus member profile');
        }
        return true;
      }),
      // redirect failed attempts to login page
      // eslint-disable-next-line rxjs/no-implicit-any-catch
      catchError((e: any) => {
        if (e.message === 'User not logged in') {
          return of(this.deny(state.url));
        } else {
          return of(this.reject(lang));
        }
      })
    );
  }

  private deny(redirectPath: string): false {
    this.loginService.redirectToLogin({ redirectPath });
    return false;
  }

  private reject(lang): false {
    this.router.navigateByUrl(`/${lang}`, { replaceUrl: true });
    return false;
  }
}
